import React, { useState, useEffect } from "react";
import { X, Rocket, Bell, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api/axios";

/**
 * Coming Soon Modal Component
 * 
 * Displays a modal for features launching in June 2026.
 * Collects user emails for the waitlist with context about:
 * - Source page
 * - Selected language
 * - Logged in status
 * - Timestamp
 */
const ComingSoonModal = ({ 
  isOpen, 
  onClose, 
  featureName = "Live Agents",
  launchDate = "June 2026",
  source = "unknown"
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Get user context
  const token = localStorage.getItem("token");
  const isLoggedIn = !!token;
  const selectedLanguage = localStorage.getItem("passportTalk_primaryLanguage") || "en";
  
  // Pre-fill email if user is logged in
  useEffect(() => {
    if (isOpen && isLoggedIn) {
      // Try to get user email from stored user data
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          if (userData.email) {
            setEmail(userData.email);
          }
        } catch (e) {
          console.log("Could not parse stored user data");
        }
      }
    }
  }, [isOpen, isLoggedIn]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      // Don't reset email if user is logged in
      if (!isLoggedIn) {
        setEmail("");
      }
    }
  }, [isOpen, isLoggedIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post("/waitlist/notify-me", {
        email: email.trim().toLowerCase(),
        source: source,
        feature: featureName,
        language: selectedLanguage,
        is_logged_in: isLoggedIn,
        timestamp: new Date().toISOString()
      });
      
      setSubmitted(true);
      toast.success("You're on the list! We'll notify you when Live Agents launch.");
    } catch (error) {
      console.error("Waitlist signup error:", error);
      // Still show success even if backend fails - we'll store locally as backup
      const existingWaitlist = JSON.parse(localStorage.getItem("passportTalk_liveAgentWaitlist") || "[]");
      if (!existingWaitlist.includes(email)) {
        existingWaitlist.push(email);
        localStorage.setItem("passportTalk_liveAgentWaitlist", JSON.stringify(existingWaitlist));
      }
      setSubmitted(true);
      toast.success("You're on the list!");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="coming-soon-modal-overlay"
    >
      <div 
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        data-testid="coming-soon-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors z-10"
          data-testid="coming-soon-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#0EA5E9] to-[#10b981] p-8 pt-10 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Rocket className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold font-outfit mb-2">
            Coming Soon
          </h2>
          <p className="text-white/90">
            {featureName} launching {launchDate}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {!submitted ? (
            <>
              <p className="text-slate-600 text-center mb-6">
                Connect with real bilingual experts for live assistance with housing, banking, government paperwork, and more.
              </p>

              {/* Features Preview */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <span>1-on-1 video/WhatsApp sessions</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <span>Bilingual experts in your language pair</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <span>Help with real-world situations</span>
                </div>
              </div>

              {/* Email Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="waitlist-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Be first to access Live Agents when we launch
                  </label>
                  <input
                    id="waitlist-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none transition-all"
                    data-testid="coming-soon-email-input"
                    disabled={loading}
                  />
                  <p className="text-xs text-slate-500 mt-2">VIP users get priority access</p>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-[#0EA5E9] to-[#10b981] text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="coming-soon-notify-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Adding you...</span>
                    </>
                  ) : (
                    <>
                      <Bell className="w-5 h-5" />
                      <span>Get Early Access</span>
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">You're on the list!</h3>
              <p className="text-slate-600 mb-6">
                We'll email you as soon as Live Agents are available.
              </p>
              <button
                onClick={onClose}
                className="px-8 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-all"
                data-testid="coming-soon-close-btn"
              >
                Got it
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComingSoonModal;
